import React, { useState, useEffect } from "react";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import AddCustomerModal from "./AddCustomerModal";
import { TextField, Button } from "@mui/material";

type IClient = {
  _id?: string;
  fullName: string;
  email?: string;
  phone?: string;
  address?: string;
  loyaltyPoints?: number;
};

type Props = {
  customer: IClient | null;
  setCustomer: (c: IClient | null) => void;
};

const WALK_IN: IClient = {
  fullName: "Walk-in Customer"
};

const CustomerEntryPanel: React.FC<Props> = ({ customer, setCustomer }) => {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IClient[]>([]);
  const [searching, setSearching] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Live search
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get(`/clients?search=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data.clients || res.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [query, token]);

  // Set Walk-in customer
  const handleWalkIn = () => {
    setCustomer({ ...WALK_IN, _id: undefined, email: "", phone: "", address: "" });
    setQuery("");
    setResults([]);
  };

  // Clear selection
  const clearSelection = () => {
    setCustomer(null);
    setQuery("");
    setResults([]);
  };

  return (
    <div>
      <label className="block font-semibold mb-1">Customer:</label>
      <div className="flex gap-2 mb-2">
        <TextField
          fullWidth
          placeholder="Search by name, email or phone..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={!!customer && customer.fullName !== "Walk-in Customer"}
          size="small"
        />
        <Button variant="outlined" onClick={() => setShowModal(true)} disabled={!!customer && customer.fullName !== "Walk-in Customer"}>
          + Add
        </Button>
        <Button variant="outlined" onClick={handleWalkIn} disabled={!!customer && customer.fullName === "Walk-in Customer"}>
          Walk-in
        </Button>
        {customer && (
          <button
            className="ml-2 px-3 py-1 bg-red-100 rounded hover:bg-red-300"
            onClick={clearSelection}
            type="button"
          >
            ✕
          </button>
        )}
      </div>
      {searching && !customer && (
        <div className="text-xs text-gray-400 mt-1">Searching...</div>
      )}
      {/* Suggestions dropdown */}
      {!customer && results.length > 0 && (
        <ul className="border bg-white rounded shadow-md max-h-52 overflow-auto z-10 relative">
          {results.map(c => (
            <li
              key={c._id}
              className="px-3 py-2 hover:bg-blue-100 cursor-pointer space-y-1"
              onClick={() => {
                setCustomer(c);
                setQuery("");
                setResults([]);
              }}
            >
              <div className="font-semibold">{c.fullName}</div>
              <div className="flex flex-wrap gap-x-4 text-xs text-gray-600">
                {c.email && <span>Email: {c.email}</span>}
                {c.phone && <span>Phone: {c.phone}</span>}
                {c.address && <span>Address: {c.address}</span>}
                {typeof c.loyaltyPoints === "number" && (
                  <span className="text-yellow-700 font-semibold">
                    Points: {c.loyaltyPoints}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {/* Selected customer */}
      {customer && (
        <div className="mt-2 p-2 rounded bg-blue-50 text-blue-900 flex items-center justify-between">
          <div>
            <div className="font-semibold">{customer.fullName}</div>
            <div className="flex flex-wrap gap-x-4 text-xs text-gray-700">
              {customer.email && <span>Email: {customer.email}</span>}
              {customer.phone && <span>Phone: {customer.phone}</span>}
              {customer.address && <span>Address: {customer.address}</span>}
              {typeof customer.loyaltyPoints === "number" && (
                <span className="text-yellow-700 font-semibold">
                  Points: {customer.loyaltyPoints}
                </span>
              )}
            </div>
          </div>
          <button
            className="ml-3 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100 rounded"
            onClick={clearSelection}
          >Clear</button>
        </div>
      )}
      {/* Add Modal */}
      <AddCustomerModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onCreated={c => {
          setCustomer(c);
          setShowModal(false);
          setQuery("");
          setResults([]);
        }}
      />
    </div>
  );
};

export default CustomerEntryPanel;


